import {
  coordinateToDistance,
  distanceToAppromateTransportationTime,
} from './distance_util';

import {
  TRANSPORTATION_TYPE_DRIVING,
  TRANSPORTATION_TYPE_TRANSIT,
  TRANSPORTATION_TYPE_WALKING,
  TRANSPORTATION_TYPE_AIRPLANE,
  TRANSPORTATION_TYPE_TRANSIT_FROM_WEB,
  TRANSPORTATION_TYPE_DRIVING_FROM_WEB,
  TRANSPORTATION_TYPE_WALKING_FROM_WEB,
  TRANSPORTATION_TYPE_OSRM_DRIVING,
  TRANSPORTATION_TYPE_OSRM_WALKING,
} from './constants';
import { TRANSPORTATION_TIME } from '../constants/itinerary';

const getKey = number => {
  const randomNumber = `_${Math.random()
    .toString(36)
    .substr(3, 15)}`;
  const key = randomNumber + Math.floor(Date.now()) + number;
  return key.toString();
};

const sortingByPoiSequence = (a, b) =>
  a.poiSequenceIndex > b.poiSequenceIndex ? 1 : -1;

const generateFormattedPois = itinerary => {
  const { dayCount, pois, startTime } = itinerary;
  const formattedPois = {};
  // formatted pois
  // {
  // "1":{id:'id1',
  //     dayArray:[poi1,poi2,poi3]
  //     },
  // "2":{id:'id2',
  //     dayArray:[poi1,poi2,poi3]
  //     },
  // }
  // eslint-disable-next-line no-unused-vars
  for (const p of pois) {
    const daySequence = p.daySequence;
    const daySequenceStr = daySequence.toString();
    if (formattedPois[daySequenceStr]) {
      const dayArray = formattedPois[daySequenceStr].dayArray;
      const poiMidArray = formattedPois[daySequenceStr].poiMidArray;
      dayArray.push(p);
      poiMidArray.push(p._id);
    } else {
      const dayArray = [];
      const poiMidArray = [];
      dayArray.push(p);
      poiMidArray.push(p._id);
      const key = getKey(daySequence);
      formattedPois[p.daySequence.toString()] = {
        id: key,
        dayArray,
        poiMidArray,
      };
    }
  }
  // // console.log('formattedPois = ', formattedPois);

  // sorting
  for (let i = 1; i <= parseInt(dayCount, 10); i++) {
    if (formattedPois[i.toString()]) {
      let dayArray = formattedPois[i.toString()].dayArray;
      dayArray = dayArray.sort(sortingByPoiSequence);
    } else {
      const key = getKey(i);
      formattedPois[i.toString()] = { id: key, dayArray: [] };
    }
  }

  // calculate arrival time ,departure time and sequence
  calculateStartTimeAndSequence(dayCount, formattedPois, startTime);
  return formattedPois;
};

// refactor (calculateStartTimeAndSequence, calculateTimeForFormattedPoisByDays)
// calculate all start time, end time and sequece for fetch pois success
const calculateStartTimeAndSequence = (
  dayCount,
  formattedPois,
  startTimeObject,
) => {
  let sequence = 1;
  for (let i = 1; i <= parseInt(dayCount, 10); i++) {
    const dayArray = formattedPois[i.toString()].dayArray;
    let startTime = parseInt(startTimeObject[i.toString()], 10);
    for (let j = 0; j < dayArray.length; j++) {
      // console.log(' j = ', j);
      const poi = dayArray[j];
      const {
        stayTime,
        customizeTransportationTimeFlag,
        customizeTransportationTime,
        transportationTime,
        timeZone,
      } = poi;

      poi.startTime = startTime;
      poi.endTime = poi.startTime + parseInt(stayTime, 10);
      poi.sequence = sequence;

      // transportation time
      const nextPoi = dayArray[j + 1];
      // remove flag and reassign if needed
      const flag = poi.approximateTransportationTimeFlag;
      delete poi.approximateTransportationTimeFlag;
      if (
        (parseInt(transportationTime, 10) === -1 || flag === true) &&
        nextPoi
      ) {
        const {
          transportationType,
          location: { lat, lng },
        } = poi;
        // coordinate to distance to time
        const distance = coordinateToDistance(
          lat,
          lng,
          nextPoi.location.lat,
          nextPoi.location.lng,
        );
        poi.transportationTime = distanceToAppromateTransportationTime(
          distance,
          transportationType,
        );
        poi.approximateTransportationTimeFlag = true;
      }

      // start time for next poi
      const poiTransportationTime =
        customizeTransportationTimeFlag === '1'
          ? customizeTransportationTime
          : poi.transportationTime;
      let timeDifference = 0;
      if (nextPoi) {
        // time difference between two timezones
        const { timeZone: nextTimeZone } = nextPoi;
        timeDifference =
          secondsFromUTC(nextTimeZone) - secondsFromUTC(timeZone);
      }
      startTime =
        poi.endTime + parseInt(poiTransportationTime, 10) + timeDifference;

      sequence++;
    }
  }
  return formattedPois;
};

const secondsFromUTC = timeZone => {
  const dateString = new Date().toLocaleString('en-US', {
    timeZone,
  });
  const date = new Date(dateString);
  return date.getTime() / 1000;
};

const calculateTimeForFormattedPoisByDays = (
  daySequenceArray,
  startTimeObject,
  formattedPois,
) => {
  const updatedFormattedPois = formattedPois;
  daySequenceArray.forEach(day => {
    const { id, dayArray: dayPoiArray } = formattedPois[parseInt(day, 10)];
    let startTime = parseInt(startTimeObject[day.toString()], 10);
    const newDayPoiArray = dayPoiArray.map(poi => {
      const {
        stayTime,
        customizeTransportationTimeFlag,
        customizeTransportationTime,
        transportationTime,
        timeZone,
      } = poi;
      const endTime = startTime + parseInt(stayTime, 10);
      const newPoi = { ...poi, startTime, endTime };
      const originTransportTime =
        transportationTime === TRANSPORTATION_TIME.CALCULATING
          ? '0'
          : transportationTime;
      let transitTime =
        customizeTransportationTimeFlag === TRANSPORTATION_TIME.CUSTOM
          ? customizeTransportationTime
          : originTransportTime;
      // startTime = endTime + parseInt(transitTime, 0);

      // transportation time & start time (time difference)
      const nextPoiIndex = dayPoiArray.indexOf(poi) + 1;
      let timeDifference = 0;
      if (dayPoiArray.length > nextPoiIndex) {
        const nextPoi = dayPoiArray[nextPoiIndex];
        // remove flag and reassign if needed
        const flag = newPoi.approximateTransportationTimeFlag;
        delete newPoi.approximateTransportationTimeFlag;
        if (
          (parseInt(transportationTime, 10) === -1 || flag === true) &&
          nextPoi
        ) {
          const {
            transportationType,
            location: { lat, lng },
          } = poi;

          if (customizeTransportationTimeFlag !== TRANSPORTATION_TIME.CUSTOM) {
            const distance = coordinateToDistance(
              lat,
              lng,
              nextPoi.location.lat,
              nextPoi.location.lng,
            );
            const approximateTime = distanceToAppromateTransportationTime(
              distance,
              transportationType,
            );
            newPoi.transportationTime = approximateTime;
            newPoi.approximateTransportationTimeFlag = true;
            transitTime = approximateTime;
          } else {
            newPoi.transportationTime = transitTime;
            newPoi.approximateTransportationTimeFlag = false;
          }
        }

        // start time and time timeDifference for next poi
        if (nextPoi) {
          // time difference between two timezones
          const { timeZone: nextTimeZone } = nextPoi;
          timeDifference =
            secondsFromUTC(nextTimeZone) - secondsFromUTC(timeZone);
        }
      }
      startTime = endTime + parseInt(transitTime, 10) + timeDifference;
      return newPoi;
    });

    updatedFormattedPois[day] = { id, dayArray: newDayPoiArray };
  });
};

/** @method directionModeClass: 按照傳入 transportationType 回傳對應交通工具 icon style class，原本寫死在 itinerary 的 methods ，因為要共用 collection Page、Article Page 而抽出到 util 給大家共用。
 * @param {string} transportationType (Required)poi 資料裡帶的交通工具 type 編號
 * @returns {string} className 對應交通工具 icon style class
 * @description 不同 type 和編號對應有很多原因...詳情得問前人
 */
const directionModeClass = transportationType => {
  switch (transportationType) {
    case TRANSPORTATION_TYPE_DRIVING: // driving
      return 'gps-mode-car';
    case TRANSPORTATION_TYPE_TRANSIT: // public transit
      return 'gps-mode-bus';
    case TRANSPORTATION_TYPE_WALKING: // walking
      return 'gps-mode-walk';
    case TRANSPORTATION_TYPE_AIRPLANE: // airplane
      return 'gps-mode-airplane';
    case TRANSPORTATION_TYPE_TRANSIT_FROM_WEB: // transit from web
      return 'gps-mode-bus';
    case TRANSPORTATION_TYPE_DRIVING_FROM_WEB: // driving from web
      return 'gps-mode-car';
    case TRANSPORTATION_TYPE_WALKING_FROM_WEB: // walking from web
      return 'gps-mode-walk';
    case TRANSPORTATION_TYPE_OSRM_DRIVING: // driving OSRM
      return 'gps-mode-car';
    case TRANSPORTATION_TYPE_OSRM_WALKING: // walking OSRM
      return 'gps-mode-walk';
    default:
      return 'gps-mode-car';
  }
};

/** formatDayHasPoi 計算 day 天數與對應景點
 * @param {object} data 需包含 dayCount 與 pois 兩參數 (pois 為單層陣列，例如 第一天第二天..第五天..etc 的 poi object 都在陣列同一層級)
 * @description 後端 getPoisOfTrip 給的資料格式有給天數和全部的景點資料(array)，但是這兩個資料是分開的，全部的景點 poi array 格式沒按照天數去做資料分第二層，全部都是同一層 array
 * 故前端還需自己計算哪些天對應哪幾筆景點
 * @example 回傳物件，key 對應天數 '1' 第一天、'2' 第二天，value 才是對應景點
 * {
 *  '1': [ {poi1}, {poi2}, {poi3} ],
 *  '2': [ {poi4} ],
 *  '3': [ {poi5}, {poi6}, ]
 * }
 */
const formatDayHasPoi = data => {
  const dayCount = Number(data.dayCount);
  const dayWithPoi = {};

  for (let i = 1; i <= dayCount; i++) {
    const poiday = data.pois.filter(item => item.daySequence === i);
    // dayWithPoi.push(poiday);
    dayWithPoi[i] = poiday;
  }

  return dayWithPoi;
};

export {
  generateFormattedPois,
  calculateTimeForFormattedPoisByDays,
  directionModeClass,
  formatDayHasPoi,
};
